import { useEffect, useState } from "react";
import API_BASE_URL from "../config";

// Lucide React Icons (inline SVG)
const Icons = {
    Plus: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
    ),
    X: () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
    ),
    Edit3: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
        </svg>
    ),
    Trash2: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
    ),
    Search: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
        </svg>
    ),
    ChevronLeft: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
    ),
    ChevronRight: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
    ),
    Shield: ({ size = 36 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
    ),
    CheckCircle: () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
    ),
    XCircle: () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
    ),
    Users: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
    ),
};

export default function UserLevelsPage({ setCurrentPage }) {
    const [userLevels, setUserLevels] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(false);
    const [accessViewAllData, setAccessViewAllData] = useState(false);
    const [accessVIewUserOnly, setaccessVIewUserOnly] = useState(false);

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);

    const [userLevelName, setUserLevelName] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [search, setSearch] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    // ---------------- API FUNCTIONS ---------------- //

    const fetchUserLevels = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/UserLevels`);
            if (!res.ok) throw new Error("Failed to fetch user levels");

            const data = await res.json();

            // Map to handle both camelCase and PascalCase
            const mapped = data.map((l) => ({
                id: l.id || l.Id,
                userLevelName: l.userLevelName || l.UserLevelName,
                isActive: l.isActive ?? l.IsActive ?? 1,
                accessViewAllData: l.accessViewAllData ?? l.AccessViewAllData ?? false,
                accessVIewUserOnly: l.accessVIewUserOnly ?? l.accessVIewUserOnly ?? false,
            }));


            setUserLevels(mapped);
            setFiltered(mapped);
        } catch (error) {
            console.error(error);
            alert(`Error: Failed to fetch user levels\n${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const createUserLevel = async () => {
        if (!userLevelName.trim()) {
            alert("Oops!\nPlease fill in all required fields");
            return;
        }

        const body = {
            UserLevelName: userLevelName.trim(),
            IsActive: isActive ? 1 : 0,
            AccessViewAllData: accessViewAllData ? true : false,
            accessVIewUserOnly: accessVIewUserOnly ? true : false,
        };


        try {
            const res = await fetch(`${API_BASE_URL}api/UserLevels`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Failed to create user level");
            }

            setOpenCreate(false);
            clearForm();
            fetchUserLevels();
            alert("Success!\nUser level created successfully");
        } catch (err) {
            console.error(err);
            alert(`Error: ${err.message}`);
        }
    };

    const updateUserLevel = async () => {
        if (!userLevelName.trim()) {
            alert("Oops!\nPlease fill in all required fields");
            return;
        }

        const body = {
            id: selectedLevel.id, // ✅ REQUIRED
            userLevelName: userLevelName.trim(),
            isActive: isActive ? 1 : 0,
            accessViewAllData: accessViewAllData, // boolean
            accessVIewUserOnly: accessVIewUserOnly, // ⚠️ EXACT TYPO FROM API
        };

        try {
            const res = await fetch(
                `${API_BASE_URL}api/UserLevels/${selectedLevel.id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                }
            );

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Failed to update user level");
            }

            setOpenEdit(false);
            setSelectedLevel(null);
            clearForm();
            fetchUserLevels();

            alert("Updated!\nUser level updated successfully");
        } catch (err) {
            console.error(err);
            alert(`Error: ${err.message}`);
        }
    };



    const deleteUserLevel = async (level) => {
        setSelectedLevel(level);
        setOpenDelete(true);
    };

    const confirmDelete = async () => {
        try {
            const res = await fetch(
                `${API_BASE_URL}api/UserLevels/${selectedLevel.id}`,
                { method: "DELETE" }
            );
            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "Failed to delete user level");
            }
            setOpenDelete(false);
            setSelectedLevel(null);
            fetchUserLevels();
            alert("Deleted!\nUser level has been deleted");
        } catch (err) {
            console.error(err);
            alert(`Error: ${err.message}`);
        }
    };

    // ---------------- USE EFFECTS ---------------- //

    useEffect(() => {
        fetchUserLevels();
    }, []);

    useEffect(() => {
        if (!search.trim()) {
            setFiltered(userLevels);
        } else {
            const term = search.toLowerCase();
            setFiltered(
                userLevels.filter(
                    (l) =>
                        l.userLevelName?.toLowerCase().includes(term)
                )
            );
        }
        setPage(1);
    }, [search, userLevels]);

    // ---------------- HELPERS ---------------- //

    const clearForm = () => {
        setUserLevelName("");
        setIsActive(true);
        setAccessViewAllData(false);
        setaccessVIewUserOnly(false);
    };


    const openEditModal = (l) => {
        setSelectedLevel(l);
        setUserLevelName(l.userLevelName);
        setIsActive(l.isActive === 1 || l.isActive === true);
        setAccessViewAllData(l.accessViewAllData === true || l.accessViewAllData === 1);
        setaccessVIewUserOnly(l.accessVIewUserOnly === true || l.accessVIewUserOnly === 1);
        setOpenEdit(true);
    };


    // ---------------- RETURN UI ---------------- //
    return (
        <div className="min-h-screen from-purple-50 to-indigo-50 p-8">
            {/* BACK BUTTON */}
            {setCurrentPage && (
                <button
                    onClick={() => setCurrentPage("references")}
                    className="mb-2 flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors group ml-8"
                >
                    <span className="group-hover:-translate-x-1 transition-transform inline-block">
                        <Icons.ChevronLeft />
                    </span>
                    Back to References
                </button>
            )}

            {/* HEADER */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <span className="text-blue -600">
                                <Icons.Shield size={36} />
                            </span>
                            User Levels
                        </h1>
                        <p className="text-gray-600 mt-2">Manage user access levels and permissions</p>
                    </div>
                    <button
                        onClick={() => setOpenCreate(true)}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                        <Icons.Plus /> Create User Level
                    </button>
                </div>
            </div>

            {/* SEARCH BAR */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <Icons.Search />
                    </span>
                    <input
                        type="text"
                        placeholder="Search by level name..."
                        className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                {loading ? (
                    <div className="flex justify-center items-center py-16">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[640px]">
                                <thead>
                                    <tr className="bg-gradient-to-r from-blue-600 to-blue-600 text-white">
                                        <th className="px-6 py-4 text-left text-sm font-semibold">ID</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold">User Level Name</th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold">
                                            View All
                                        </th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold">
                                            View Own
                                        </th>

                                        <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {paginated.length > 0 ? (
                                        paginated.map((level) => (
                                            <tr
                                                key={level.id}
                                                className="hover:bg-blue-50 transition-colors"
                                            >
                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-purple-800 font-semibold text-sm">
                                                        {level.id}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                                                            <Icons.Users />
                                                        </div>
                                                        <span className="font-semibold text-gray-800">
                                                            {level.userLevelName}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex justify-center">
                                                        {level.isActive === 1 || level.isActive === true ? (
                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                <Icons.CheckCircle />
                                                                Active
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                <Icons.XCircle />
                                                                Inactive
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    {level.accessViewAllData ? (
                                                        <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                                                            <Icons.CheckCircle /> Yes
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                                                            <Icons.XCircle /> No
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 text-center">
                                                    {level.accessVIewUserOnly ? (
                                                        <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                                                            <Icons.CheckCircle /> Yes
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                                                            <Icons.XCircle /> No
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4">
                                                    <div className="flex justify-center gap-2">
                                                        <button
                                                            onClick={() => openEditModal(level)}
                                                            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                            title="Edit"
                                                        >
                                                            <Icons.Edit3 />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteUserLevel(level)}
                                                            className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                            title="Delete"
                                                        >
                                                            <Icons.Trash2 />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-16 text-center">
                                                <div className="flex flex-col items-center gap-3 text-gray-400">
                                                    <Icons.Shield size={48} />
                                                    <p className="text-lg font-medium">
                                                        {search.trim()
                                                            ? `No user levels match "${search}"`
                                                            : "No user levels found"}
                                                    </p>
                                                    <p className="text-sm">
                                                        {search.trim()
                                                            ? "Try adjusting your search"
                                                            : "Create your first user level to get started"}
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* PAGINATION */}
                        {paginated.length > 0 && (
                            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                                    <span className="text-sm text-gray-600">
                                        Showing {(page - 1) * perPage + 1} to{" "}
                                        {Math.min(page * perPage, filtered.length)} of {filtered.length}{" "}
                                        user levels
                                    </span>
                                    <div className="flex items-center gap-3">
                                        <button
                                            disabled={page <= 1}
                                            onClick={() => setPage(page - 1)}
                                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                        >
                                            <Icons.ChevronLeft />
                                        </button>
                                        <span className="text-sm font-semibold text-gray-700">
                                            Page {page} of {totalPages || 1}
                                        </span>
                                        <button
                                            disabled={page >= totalPages}
                                            onClick={() => setPage(page + 1)}
                                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                        >
                                            <Icons.ChevronRight />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* MODAL */}
            {(openCreate || openEdit) && (
                <Modal
                    title={openCreate ? "Create New User Level" : "Edit User Level"}
                    close={() => {
                        setOpenCreate(false);
                        setOpenEdit(false);
                        setSelectedLevel(null);
                        clearForm();
                    }}
                >
                    <div className="space-y-4">
                        <Input
                            label="User Level Name*"
                            value={userLevelName}
                            onChange={setUserLevelName}
                            placeholder="e.g., SYSTEM ADMIN, DEPARTMENT HEAD"
                            required
                        />
                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                id="isActive"
                                className="h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                            />
                            <label htmlFor="isActive" className="text-sm font-semibold text-gray-700 cursor-pointer">
                                Active User Level
                            </label>
                        </div>
                        <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={accessViewAllData}
                                    onChange={(e) => setAccessViewAllData(e.target.checked)}
                                    className="h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                                />
                                <span className="text-sm font-semibold text-gray-700">
                                    Can View All Data
                                </span>
                            </label>

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={accessVIewUserOnly}
                                    onChange={(e) => setaccessVIewUserOnly(e.target.checked)}
                                    className="h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                                />
                                <span className="text-sm font-semibold text-gray-700">
                                    Can View Own Data Only
                                </span>
                            </label>
                        </div>

                    </div>

                    <button
                        onClick={openCreate ? createUserLevel : updateUserLevel}
                        className="mt-6 w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                    >
                        {openCreate ? "Create User Level" : "Update User Level"}
                    </button>
                </Modal>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {openDelete && selectedLevel && (
                <Modal
                    title="Delete User Level"
                    close={() => {
                        setOpenDelete(false);
                        setSelectedLevel(null);
                    }}
                >
                    <div className="space-y-4">
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                            <p className="text-gray-700 text-center">
                                Are you sure you want to delete
                            </p>
                            <p className="text-lg font-bold text-red-600 text-center mt-2">
                                {selectedLevel.userLevelName}?
                            </p>
                            <p className="text-sm text-gray-600 text-center mt-2">
                                This action cannot be undone.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button
                            onClick={() => {
                                setOpenDelete(false);
                                setSelectedLevel(null);
                            }}
                            className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-xl hover:bg-gray-300 transition-all font-semibold"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmDelete}
                            className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-xl hover:from-red-700 hover:to-red-800 transition-all font-semibold shadow-lg hover:shadow-xl"
                        >
                            Delete
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// Modal Component
function Modal({ title, close, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={close}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
                >
                    <Icons.X />
                </button>
                <h2 className="text-3xl font-bold text-gray-800 mb-8 flex items-center gap-3">
                    <span className="text-purple-600">
                        <Icons.Shield size={28} />
                    </span>
                    {title}
                </h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", placeholder = "", required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all outline-none"
            />
        </div>
    );
}